import React, { ComponentProps, ElementType, FC, MouseEvent, ReactNode, useCallback } from 'react';
import useHistory from './useHistory';
import RELOAD_TARGETS from './reloadTargets';

const isSameHost = (href?: string) => {
  if (!href) {
    return false;
  }

  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return true;
  }

  return url.hostname === window.location.hostname;
};

const isReloadTarget = (url?: string) => {
  if (!url) {
    return false;
  }

  try {
    const pathname = new URL(url).pathname;
    return RELOAD_TARGETS.indexOf(pathname.toLowerCase()) >= 0;
  } catch {
    return false;
  }
};

interface IRouterLink {
  children?: ReactNode;
  Component?: FC | 'a';
  disable?: boolean;
  href?: string;
  onBeforeNavigate?: (e: React.MouseEvent<HTMLElement>) => void;
  onClick?: () => void;
  onPostNavigate?: (e: React.MouseEvent<HTMLElement>) => void;
  rel?: string;
  target?: string;
}

const Link = <TComponent extends ElementType>(
  {
    Component = 'a',
    children,
    href,
    disable,
    onBeforeNavigate,
    onClick,
    onPostNavigate,
    rel,
    target,
    ...linkProps
  }: IRouterLink & Partial<ComponentProps<TComponent>>,
  ref: React.ForwardedRef<HTMLAnchorElement>,
) => {
  const { push } = useHistory();
  const click = useCallback(
    (e: MouseEvent<TComponent | HTMLAnchorElement>) => {
      const aTag = (e.target as HTMLElement)?.closest('a');
      const shouldReload =
        !aTag?.hasAttribute('data-prevent-reload') &&
        (aTag?.hasAttribute('data-reload') || isReloadTarget(aTag?.href));

      if (onBeforeNavigate) {
        onBeforeNavigate(e as React.MouseEvent<HTMLElement>);
      }

      if (shouldReload) {
        if (onClick) {
          onClick();
        }
        return;
      }

      if (isSameHost(href)) {
        e.preventDefault();
        const current = window.location.href.substring(window.location.origin.length);

        if (onClick) {
          onClick();
        } else if (e.ctrlKey || e.metaKey) {
          window.open(href, '_blank');
        } else if (e.shiftKey) {
          window.open(href);
        } else if (href && href !== current) {
          push(href);
        }

        if (onPostNavigate && !e.ctrlKey) {
          onPostNavigate(e as React.MouseEvent<HTMLElement>);
        }
      } else {
        e.preventDefault();
        window.open(href, '_blank');
      }
    },
    [href, onClick],
  );

  return (
    <Component
      onClick={(!disable && click) || undefined}
      href={href || '!#'}
      target={target}
      ref={ref}
      rel={rel}
      {...linkProps}>
      {children}
    </Component>
  );
};

export default React.forwardRef(Link);
